import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
	static values = {
		event: String,
		payload: Object,
		once: { type: Boolean, default: true },
		removeAfter: { type: Boolean, default: false },
		includeQuizInput: { type: Boolean, default: false },
		trackOnce: { type: Boolean, default: false },
		quizInputField: String,
		ltvBands: Object,
	};

	getNavItem(link) {
		if (!link) return "";

		const ariaLabel = link.getAttribute?.("aria-label")?.trim?.();
		if (ariaLabel) return ariaLabel;

		const clone = link.cloneNode(true);
		clone
			.querySelectorAll?.(".visually-hidden, .sr-only")
			.forEach((el) => el.remove());

		return (clone.textContent || "").replace(/\s+/g, " ").trim();
	}

	getQuizInput(form) {
		if (!form) return "";

		const checkedBoxes = form.querySelectorAll('input[type="checkbox"]:checked');
		const selectedRadio = form.querySelector('input[type="radio"]:checked');
		const selectedText = form.querySelector('input[type="text"]');
		const selectedNumber = form.querySelector('input[type="number"]');
		const selectedSelect = form.querySelector("select");

		if (checkedBoxes.length > 0) {
			return Array.from(checkedBoxes)
				.map((checkbox) => checkbox.value || "")
				.join(",");
		}
		if (selectedRadio) return selectedRadio.value || "";
		if (selectedText) return selectedText.value || "";
		if (selectedNumber) return selectedNumber.value || "";
		if (selectedSelect) return selectedSelect.value || "";
		return "";
	}

	predictedLtv(weight) {
		const { floors = [], unknown } = this.ltvBandsValue;
		if (weight === null) return unknown;

		const band = floors.filter(([floor]) => weight >= floor).pop() || floors[0];
		return band ? band[1] : unknown;
	}

	connect() {
		if (this.onceValue && this.element.dataset.analyticsTracked === "true") {
			return;
		}

		if (!this.hasPayloadValue && !this.hasEventValue) {
			return;
		}

		// Action-driven elements push on their action. A load push would leave their payload in GTM's data model.
		if (this.element.dataset.action?.includes("analytics#track")) {
			return;
		}

		const payload = this.buildPayload();
		if (!payload) return;

		this.push(payload);
		this.element.dataset.analyticsTracked = "true";

		if (this.removeAfterValue) {
			this.element.remove();
		}
	}

	buildPayload() {
		const payload = this.hasPayloadValue ? this.payloadValue : {};
		if (!payload || typeof payload !== "object") return null;

		if (this.hasEventValue && this.eventValue) {
			return { event: this.eventValue, ...payload };
		}

		return payload;
	}

	track(event) {
		if (!event) return;
		if (event.target?.closest?.("[data-analytics-ignore]")) return;

		if (
			this.trackOnceValue &&
			this.element.dataset.analyticsTrackedTrack === "true"
		) {
			return;
		}

		const params = event.params || {};
		const eventName =
			params.event || (this.hasEventValue ? this.eventValue : null);
		if (!eventName) return;

		const basePayload = this.hasPayloadValue ? this.payloadValue : {};
		const payload = { ...(basePayload || {}) };

		Object.entries(params).forEach(([key, value]) => {
			if (key === "event") return;
			payload[key] = value;
		});

		if (this.includeQuizInputValue && payload.quiz_input === undefined) {
			const form = event.target?.closest?.("form");
			payload.quiz_input = this.quizInputFieldValue
				? form?.elements?.namedItem(this.quizInputFieldValue)?.value || ""
				: this.getQuizInput(form);
		}

		if (this.hasLtvBandsValue) {
			const weight = Number.parseInt(payload.quiz_input, 10);
			payload.dog_weight = Number.isNaN(weight) ? null : weight;
			payload.predicted_ltv = this.predictedLtv(payload.dog_weight);
		}

		if (
			eventName === "navClick" &&
			(payload.navItem === undefined ||
				payload.navItem === null ||
				payload.navItem === "")
		) {
			const link = event.target?.closest?.("a");
			if (!link) return;
			payload.navItem = this.getNavItem(link);
			if (!payload.navItem) return;
		}

		this.push({ event: eventName, ...payload });

		if (this.trackOnceValue) {
			this.element.dataset.analyticsTrackedTrack = "true";
		}
	}

	push(payload) {
		const userId = document.body?.dataset?.analyticsUserId;
		if (
			userId &&
			payload &&
			typeof payload === "object" &&
			payload.user_id === undefined
		) {
			payload.user_id = userId;
		}

		window.dataLayer = window.dataLayer || [];
		if (!Array.isArray(window.dataLayer)) return;
		window.dataLayer.push(payload);
	}
}
